import type { GetServerSideProps } from 'next'
import App from '../App'
import type { ShopLocale } from '../i18n/shopLocale'
import { buildCatchAllPageProps } from './bootstrap'
import { resolveCatchAllLocaleState } from './locale'
import {
  buildLocalizedRedirectDestination,
  isBypassPath,
  mapLegacyAdminPath,
  mergeSearch,
  normalizeRepeatedShortProductPath,
} from './redirects'
import type { CatchAllPageProps } from './types'
import { resolveLegacyAdminAbsoluteDestination } from './admin-redirect'

export const resolveProtocol = (ctx: Parameters<GetServerSideProps<CatchAllPageProps>>[0]): string => {
  const forwarded = ctx.req.headers['x-forwarded-proto']
  const fromHeader = Array.isArray(forwarded)
    ? forwarded[0]
    : typeof forwarded === 'string'
      ? forwarded.split(',')[0]
      : ''
  const normalized = (fromHeader || '').trim().toLowerCase()
  if (normalized === 'https' || normalized === 'http') return normalized
  const tlsSocket = ctx.req.socket as { encrypted?: boolean }
  return tlsSocket.encrypted ? 'https' : 'http'
}

export const buildAdminAbsoluteDestination = ({
  ctx,
  pathWithSearch,
}: {
  ctx: Parameters<GetServerSideProps<CatchAllPageProps>>[0]
  pathWithSearch: string
}): string => {
  const protocol = resolveProtocol(ctx)
  return resolveLegacyAdminAbsoluteDestination({
    configuredAdminUrl: process.env.ADMIN_PUBLIC_URL,
    protocol,
    hostHeader: ctx.req.headers.host,
    pathWithSearch,
  })
}

export const buildStorefrontPageProps = async ({
  ctx,
  initialPathname,
  initialSearch,
  initialLocale,
}: {
  ctx: Parameters<GetServerSideProps<CatchAllPageProps>>[0]
  initialPathname: string
  initialSearch: string
  initialLocale: ShopLocale
}): Promise<CatchAllPageProps> =>
  buildCatchAllPageProps({
    ctx,
    initialPathname,
    initialSearch,
    initialLocale,
  })

export const resolveStorefrontRouteOutcome = async ({
  ctx,
  requestedPathname,
}: {
  ctx: Parameters<GetServerSideProps<CatchAllPageProps>>[0]
  requestedPathname: string
}) => {
  const localeState = resolveCatchAllLocaleState({
    ctx,
    requestedPathname,
  })

  const legacyAdminDestination = mapLegacyAdminPath(localeState.pathnameWithoutLocale)
  if (legacyAdminDestination) {
    const destinationPath = mergeSearch(legacyAdminDestination, localeState.initialSearch)
    return {
      redirect: {
        destination: buildAdminAbsoluteDestination({
          ctx,
          pathWithSearch: destinationPath,
        }),
        permanent: false,
      },
    } as const
  }

  const repeatedShortProductPath = normalizeRepeatedShortProductPath(localeState.pathnameWithoutLocale)
  if (repeatedShortProductPath) {
    return {
      redirect: {
        destination: buildLocalizedRedirectDestination({
          pathnameWithoutLocale: repeatedShortProductPath,
          initialSearch: localeState.initialSearch,
          locale: localeState.localeFromPath ?? localeState.resolvedLocale,
        }),
        permanent: false,
      },
    } as const
  }

  if (!localeState.hadLocalePrefix && !isBypassPath(requestedPathname)) {
    return {
      redirect: {
        destination: buildLocalizedRedirectDestination({
          pathnameWithoutLocale: localeState.pathnameWithoutLocale,
          initialSearch: localeState.initialSearch,
          locale: localeState.resolvedLocale,
        }),
        permanent: false,
      },
    } as const
  }

  const initialPathname = localeState.pathnameWithoutLocale
  const initialLocale = (localeState.localeFromPath ?? localeState.resolvedLocale) as ShopLocale

  return {
    props: await buildStorefrontPageProps({
      ctx,
      initialPathname,
      initialSearch: localeState.initialSearch,
      initialLocale,
    }),
  } as const
}

export function StorefrontPage({
  initialPathname,
  initialSearch,
  initialLocale,
  initialActiveLocales,
  initialSeo,
  siteUrl,
  initialSiteName,
  initialFaviconUrl,
}: CatchAllPageProps) {
  return (
    <App
      initialPathname={initialPathname}
      initialSearch={initialSearch}
      initialLocale={initialLocale}
      initialActiveLocales={initialActiveLocales}
      initialSeo={initialSeo}
      siteUrl={siteUrl}
      initialSiteName={initialSiteName}
      initialFaviconUrl={initialFaviconUrl}
    />
  )
}
